import { isValid, format, parse, parseISO } from 'date-fns';

export function validateDate(date: string | null | undefined) {
	if (date) {
		const parsedDate = parseISO(date);
		if (isValid(parsedDate)) {
			return parsedDate;
		}
	}

	return null;
}

/**
 * Used to solve issue with bad date-time conversion when creating a new Date object, since it will be created in the local timezone which can be one day in the past or in the future, depending on the timezone.
 * Takes in a date, and returns new Date object from which the time zone offset is removed. If the input is not a valid date or is falsy, it returns a fallback value.
 *
 * @export
 * @param {(string | undefined | null)} date - Date to validate and format
 * @param {string} [fallbackValue='N/A'] - fallback value if date is not valid
 * @returns {Date} in the same day regardless of time zone offset, if there is a valid date provided, returns fallbackValue if not.
 */

export function deLocalizedDate(date: string | Date | undefined | null, fallbackValue = 'N/A') {
	if (date) {
		const localizedDate = new Date(date);

		if (isValid(localizedDate)) {
			return new Date(localizedDate.getTime() - localizedDate.getTimezoneOffset() * -60000);
		}
	}

	return fallbackValue;
}

/**
 * Used to solve issue with bad date-time conversion when parsing date. We receive the date from API as yyyy-mm-dd
 * so new Date() will always return a date that is one day before the actual date after Midnight CET for timezones west of CET, thats why we relay on usage of {@link deLocalizedDate} func.
 *
 * @export
 * @param {(string | undefined | null)} date - Date to validate and format
 * @param {string} [dateFormat='MM/dd/yyyy'] - format of date. Uses date-fns format: https://date-fns.org/v2.29.3/docs/format
 * @param {string} [fallbackValue='N/A'] - fallback value if date is not valid
 * @returns {string} in 'MM/d/yyyy' format (or format that was passed as arg) if there is a valid date provided, returns fallbackValue if not.
 */
export function validateAndFormatDate(
	date: string | Date | undefined | null,
	dateFormat = 'MM/dd/yyyy',
	fallbackValue = 'N/A'
) {
	const deLocalizedDateValue = deLocalizedDate(date);

	if (deLocalizedDateValue instanceof Date) {
		return format(deLocalizedDateValue, dateFormat);
	}

	return fallbackValue;
}

/**
 * Used to format the dateless hours received from API into a readable format.
 *
 * @export
 * @param {(string | null)} [time] - time to format. Should be in format HH:mm:ss (e.g. 12:00:00). Secods are optional.
 * @param {string} [dateFormat='h:mm a'] - format of date. Uses date-fns format: https://date-fns.org/v2.29.3/docs/format
 * @param {string} [fallbackValue='N/A'] - fallback value if date is not valid
 * @return {string} - formatted time in format h:mm a (e.g. 12:00 PM), or any other format that was passed as arg. Returns fallbackValue if not valid time was provided.
 */
export function formatDatelessHours(time?: string | null, dateFormat = 'h:mm a', fallbackValue = 'N/A') {
	if (!time) return fallbackValue;

	const [hour, minutes] = time.split(':');

	if (!hour || !minutes) return fallbackValue;

	const toFormatTime = parse(`${hour} ${minutes}`, 'HH mm', new Date());
	const formattedTime = format(toFormatTime, dateFormat);

	return formattedTime;
}

/**
 * Used to format the date of birth received from API into US format.
 *
 * @export
 * @param {(string | undefined | null)} date - Date to format, expected to be in ISO format or partial ISO format (yyyy-mm-dd)
 * @return {string} - formatted date in format MM/dd/yyyy, or empty string if no date was provided.
 *
 */
export function formatDateOfBirthForDisplay(date: string | undefined | null) {
	if (!date) {
		return '';
	}
	const [year, month, day] = date.split('T')[0].split('-');
	return `${month}/${day}/${year}`;
}

// this utility is to be used only for static dates that have no timezone shifting,
// like someone's date of birth or a date of a visit to a specific facility that is local to the facility
export const resolveStaticDateString = (date: string | undefined | null) => {
	if (!date) return '';

	// we always receive yyyy-mm-dd from the api,
	// here instead of casting to date and formating and having issues with timzeones
	// we just re-arange the string to be mm/dd/yyyy
	const [year, month, day] = date.split('-');
	return `${month}/${day}/${year}`;
};

// this utility is to be used only for static dates that have no timezone shifting,
// like someone's date of birth or a date of a visit to a specific facility that is local to the facility
export const resolveStaticDate = (dateStr: string | undefined | null) => {
	if (!dateStr) return null;

	return new Date(dateStr.replace(/-/g, '/').replace(/T.+/, ''));
};

// reformat date and time without worrying about time zone
// useful when backend is returning time in local office time zone and we just need it reformatted
// ISO 8601 because our GraphQL always uses that
export const resolveISODateTime = (dateStr: string, outFormat: string) => {
	const date = parseISO(dateStr.slice(0, dateStr.endsWith('Z') ? -1 : -6));

	return format(date, outFormat);
};
